import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { DataDogLoggerService } from '../../../services/data-dog-logger.service';
import { PASSWORD_PATTERN } from '../../models/login.model';
import { LoginService } from '../../services/login.service';

/**
 * Sign-up entry point.
 *
 * - When `environment.auth0` is configured (dev) the actual registration form
 *   lives in Auth0 Universal Login (screen_hint=signup); this component only
 *   persists any `activation_token` and opens the Auth0 signup popup.
 * - Otherwise (prod/staging/demo) it falls back to the legacy registration
 *   form (POST /users).
 */
@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit, AfterViewInit {

  /** The legacy form is rendered behind `*ngIf="!useAuth0"`, so it is not in the
   *  DOM on first render: use a non-static query and access it defensively. */
  @ViewChild(NgForm) public form: NgForm;

  public useAuth0 = !!environment.auth0;

  public availableLanguages: {
    name: string;
    slug: string;
  }[] = environment.languages;
  public sending = false;
  public loginError = false;
  public user = {
    email: null,
    name: null,
    last_name: null,
    password: null,
    password_confirmation: null,
    invitation: null
  };
  public with_mail: boolean;
  public PASSWORD_PATTERN = PASSWORD_PATTERN;

  private token: string;
  private captchaResponse: string;
  private activationToken: string | null = null;

  /** @ignore */
  constructor(
    private router: Router,
    private loginService: LoginService,
    /** @ignore */
    public translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private dataDogLoggerService: DataDogLoggerService
  ) { }

  /** @ignore */
  ngOnInit(): void {
    this.loginService.logout(true);
    this.token = this.activatedRoute.snapshot.queryParams['activation_token'];

    if (this.useAuth0) {
      this.activationToken = this.token ?? null;
      if (this.activationToken) {
        // Persist the token so handleAuth0Callback can pick it up after the popup closes.
        sessionStorage.setItem('activation_token', this.activationToken);
      }
      return;
    }

    // Legacy registration form.
    if (this.token) {
      this.loginService.validateActivationToken(this.token).subscribe({
        next: user => {
          this.user.email = user.email;
          this.with_mail = true;
        },
        error: error => {
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
    } else {
      this.with_mail = false;
    }
  }

  /** @ignore */
  ngAfterViewInit(): void {
    // The legacy form only exists when `!useAuth0`.
    if (this.useAuth0 || !this.form) return;

    // Clear custom errors
    // TODO: directive or validator for custom errors?
    this.form.valueChanges.pipe(
      debounceTime(100)
    ).subscribe(() => {
      // Clear all errors from the entire form
      this.form.control.setErrors(null);

      // Clear the 'unique' error specifically from the 'email' control if it exists
      const emailControl = this.form.controls['email'];
      if (emailControl?.errors?.['unique']) {
        emailControl.setErrors(null);
      }
    });
  }

  /** Opens the Auth0 signup popup (dev). */
  public signUpWithAuth0(): void {
    this.sending = true;
    this.loginError = false;

    this.loginService.loginWithAuth0Popup('signup')
      .then(() => {
        this.router.navigateByUrl('companies?referral=signup');
      })
      .catch(err => {
        if (err?.message !== 'popup_closed') this.loginError = true;
        this.sending = false;
      });
  }

  /** Legacy sign-up form submit. */
  public signUp(): void {
    this.sending = true;

    if (this.token) {
      this.user.invitation = {
        activation_token: this.token
      };
    }

    this.loginService.register(this.user, this.captchaResponse).subscribe({
      next: login => {
        this.loginService.checkMailVerified();
        this.sending = false;
        this.router.navigate(['/companies']);
      },
      error: error => {
        this.sending = false;
        let message = error.error.message;
        if (message) {
          if (message.inputs && message.inputs.email && message.inputs.email.Unique) {

            this.form.controls.email.setErrors({
              "unique": true
            });

            this.form.control.setErrors({
              "not_unique_email": true
            });

          } else {

            this.form.control.setErrors({
              "remote": true
            });

          }
        } else {
          this.form.control.setErrors({
            "unknown": true
          });
        }
        this.dataDogLoggerService.warn(error.message, error.error);
      }
    });
  }

  /** Language switch. */
  public updateLanguage(slug: string): void {
    this.translateService.use(slug);
  }

  public resolved(captchaResponse: string): void {
    this.captchaResponse = captchaResponse;
  }
}
